import type { DeferredAttribution } from "@backflip/attribution"
import { FullStory, isInitialized } from "@fullstory/browser"
import { track, type AnalyticsProps } from "./analytics.client"
import { fbqTrackCustom } from "./facebookPixel.client"

/**
 * Loan-flow + registration events, mirroring the backflip-mobile event names
 * and the `submitted_values` envelope (see backflip-mobile
 * src/analytics/events/base.ts). Keeping the shapes identical lets the web and
 * mobile apps share Mixpanel/FullStory funnels.
 */

/** Mobile "<object> Submitted" envelope. */
function trackSubmitted(
  objectName: string,
  opts: {
    currentScreen: string
    toScreen?: string
    componentText: string
    submittedValues?: AnalyticsProps
  }
): void {
  track(`${objectName} Submitted`, {
    current_screen: opts.currentScreen,
    to_screen: opts.toScreen ?? "Unknown",
    component_type: "button",
    component_text: opts.componentText,
    submitted_values: opts.submittedValues ?? {},
  })
}

/** Registration complete — matches mobile "Registration Submitted". */
export function trackRegistrationSubmitted(email: string): void {
  trackSubmitted("Registration", {
    currentScreen: "Register",
    toScreen: "Loans",
    componentText: "Create Account",
    submittedValues: { email },
  })
}

/**
 * A loan application/deal was created on the intake screen (top of funnel).
 * Web has fewer screens than mobile, so this is the "started" signal; it pairs
 * with "Loan Application Submitted" for a Started -> Submitted funnel.
 */
export function trackLoanApplicationStarted(opts: {
  toScreen: string
  componentText: string
  values: AnalyticsProps
}): void {
  track("Loan Application Started", {
    current_screen: "Loan Quote",
    to_screen: opts.toScreen,
    component_type: "button",
    component_text: opts.componentText,
    submitted_values: opts.values,
  })
}

/** Borrower committed the application — matches mobile "Loan Application Submitted". */
export function trackLoanApplicationSubmitted(opts: {
  currentScreen: string
  componentText: string
  values: AnalyticsProps
}): void {
  trackSubmitted("Loan Application", {
    currentScreen: opts.currentScreen,
    toScreen: "Loan",
    componentText: opts.componentText,
    submittedValues: opts.values,
  })
}

/** Borrower declined after seeing options (web-only; no mobile equivalent). */
export function trackLoanApplicationNotInterested(
  values: AnalyticsProps
): void {
  track("Loan Application Not Interested", {
    current_screen: "Loan Options",
    component_type: "button",
    component_text: "Not interested",
    submitted_values: values,
  })
}

/** Which A/B flow variant a deal went through — matches mobile "Variant Used". */
export function trackVariantUsed(code: string): void {
  track("Variant Used", {
    variant_flag: "loan_setup_path",
    variant_value: code,
  })
}

/** UTM params of an ad-referred visit. camelCase in, snake_case on the event. */
export interface AdUtmParams {
  utmSource?: string | null
  utmMedium?: string | null
  utmCampaign?: string | null
  utmContent?: string | null
  utmTerm?: string | null
}

const AD_EVENT_NAME = "User Captured By Ad"

/**
 * Send the custom event to FullStory once its SDK is ready. Polls
 * `isInitialized()` rather than using `init`'s ready callback: the root layout
 * already consumes FullStory's single `init()` call and a second one is ignored
 * by `@fullstory/browser`. Bounded (~10s) so a blocked SDK leaves no timer.
 */
function sendAdEventToFullStoryWhenReady(
  properties: AnalyticsProps,
  attempt = 0
): void {
  if (isInitialized()) {
    try {
      FullStory("trackEvent", { name: AD_EVENT_NAME, properties })
    } catch {
      // FullStory unavailable — Mixpanel still got the event.
    }
    return
  }
  if (attempt >= 40) return
  setTimeout(() => {
    sendAdEventToFullStoryWhenReady(properties, attempt + 1)
  }, 250)
}

/**
 * A visitor arrived from an ad — matches mobile's "User Captured By Ad" event
 * name and snake_case UTM properties so web and mobile share the funnel.
 * `track()` reaches FullStory only if it is already initialized, which on a cold
 * landing it usually isn't, so we also poll — guarded to never double-send.
 */
export function trackUserCapturedByAd(utm: AdUtmParams): void {
  if (typeof window === "undefined") return

  const properties = {
    utm_source: utm.utmSource ?? null,
    utm_medium: utm.utmMedium ?? null,
    utm_campaign: utm.utmCampaign ?? null,
    utm_content: utm.utmContent ?? null,
    utm_term: utm.utmTerm ?? null,
  }

  const fullStoryReady = isInitialized()
  track(AD_EVENT_NAME, properties)

  if (!fullStoryReady) {
    sendAdEventToFullStoryWhenReady(properties)
  }
}

/**
 * The app-download button was tapped. This is the click side of install
 * attribution — the only number we control end to end, since the install itself
 * is visible only via Apple's aggregate campaign tokens or a clipboard claim.
 * Also emits a Meta custom event so download intent is usable as an ad audience.
 */
export function trackAppDownloadClicked(opts: {
  attribution: DeferredAttribution
  platform: string
  referrer: string | null
}): void {
  if (typeof window === "undefined") return

  const { attribution } = opts
  const properties = {
    utm_source: attribution.source ?? null,
    utm_medium: attribution.medium ?? null,
    utm_campaign: attribution.campaign ?? null,
    utm_content: attribution.content ?? null,
    utm_term: attribution.term ?? null,
    gclid: attribution.gclid ?? null,
    gbraid: attribution.gbraid ?? null,
    wbraid: attribution.wbraid ?? null,
    fbclid: attribution.fbclid ?? null,
    platform: opts.platform,
    referrer: opts.referrer,
  }

  track("App Download Clicked", properties)
  fbqTrackCustom("AppDownloadClicked", properties)
}

/** Tapped through to the DSCR inquiry form (EN-6184); mobile tracks `dscr_inquiry_opened`. */
export function trackDscrInquiryOpened(source: "loan_type"): void {
  track("DSCR Inquiry Opened", { source })
}
